import { useCallback, useEffect, useState } from 'react';
import { noop } from 'lodash-es';

export type SortField = 'name' | 'type' | 'modifiedAt';
export type SortOrder = 'asc' | 'desc';

export interface SortState {
  sortField: SortField;
  sortOrder: SortOrder;
}

type UseStoredSettings = SortState & { setSort: (next: SortState) => void };

const isSortState = (obj: any): obj is SortState => 'sortField' in obj && 'sortOrder' in obj;

const SETTINGS_SUFFIX = '-settings';

const buildSettingsKey = (guid: string) => guid.concat(SETTINGS_SUFFIX);
export const defaultSortState: SortState = { sortField: 'modifiedAt', sortOrder: 'desc' };

const getStoredSortState = (guid: string): SortState | null => {
  const sortState = localStorage.getItem(buildSettingsKey(guid));
  if (!sortState) {
    return null;
  }
  try {
    const parsed = JSON.parse(sortState);
    return isSortState(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const storeSortState = (guid: string, settings: SortState) =>
  localStorage.setItem(buildSettingsKey(guid), JSON.stringify(settings));

export const useSortSettings = (folderGuid?: string): UseStoredSettings => {
  const [state, setState] = useState<SortState | null>(null);

  const setSortState = useCallback(
    (next: SortState) => {
      if (folderGuid) {
        storeSortState(folderGuid, next);
        setState(next);
      }
    },
    [folderGuid, setState],
  );

  useEffect(() => {
    if (folderGuid && !state) {
      setState(getStoredSortState(folderGuid) || defaultSortState);
    }
  }, [folderGuid, setState, state]);

  return state && folderGuid
    ? { ...state, setSort: setSortState }
    : { ...defaultSortState, setSort: noop };
};
